import { cn } from "cn";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type * as React from "react";
import { useId } from "react";

// The review card's frame (apps/web pages/Visit/workspace/ReviewCard.tsx):
// amber title, "2 of 5" with previous/next, close, a progress bar, the
// flagged value in its sentence, a short explanation and the actions. As a
// card anchored next to the value from 1024px, a bottom sheet on phones.
// Positioning, focus and saving stay with the caller.

export const REVIEW_ICON_BUTTON =
  "inline-flex size-11 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-40 lg:size-8";
// The card's main action (Accept, Save) and its quiet ones (Edit, Cancel).
export const REVIEW_PRIMARY =
  "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-v3-ink px-5 text-[14px] font-semibold text-v3-on-ink transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring focus-visible:ring-offset-2 focus-visible:ring-offset-v3-panel disabled:pointer-events-none disabled:opacity-50 lg:h-10";
export const REVIEW_QUIET =
  "inline-flex h-11 items-center justify-center gap-2 rounded-full px-4 text-[14px] font-semibold text-v3-text transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50 lg:h-10";
// Remove: a quiet action in the record red (takes the value out of the note).
export const REVIEW_REMOVE =
  "inline-flex h-11 items-center justify-center gap-2 rounded-full px-4 text-[14px] font-semibold text-v3-rec-text transition-colors hover:bg-v3-rec-tint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50 lg:h-10";

interface ReviewCardShellProps {
  title: React.ReactNode;
  /** 0-based index of the value shown, and how many need review. */
  index: number;
  total: number;
  /** "2 of 5". */
  positionLabel: string;
  previousLabel: string;
  nextLabel: string;
  closeLabel: string;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
  /** The flagged value in its sentence. */
  before?: React.ReactNode;
  value: React.ReactNode;
  after?: React.ReactNode;
  explanation?: React.ReactNode;
  /** Buttons (REVIEW_PRIMARY / REVIEW_QUIET) or the edit form. */
  actions?: React.ReactNode;
  busy?: boolean;
  /** "card": floating card (position it with style). "sheet": bottom sheet. */
  layout?: "card" | "sheet";
  style?: React.CSSProperties;
  className?: string;
}

export function ReviewCardShell({
  title,
  index,
  total,
  positionLabel,
  previousLabel,
  nextLabel,
  closeLabel,
  onPrevious,
  onNext,
  onClose,
  before,
  value,
  after,
  explanation,
  actions,
  busy = false,
  layout = "card",
  style,
  className,
}: ReviewCardShellProps): React.JSX.Element {
  const titleId = useId();
  return (
    <div
      aria-labelledby={titleId}
      className={cn(
        "z-50 bg-v3-panel font-v3 text-v3-text",
        layout === "sheet"
          ? "fixed inset-x-0 bottom-0 max-h-[75dvh] overflow-y-auto rounded-t-[22px] px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-v3-float"
          : "rounded-[18px] p-4 shadow-v3-pop",
        className,
      )}
      data-testid="review-card"
      role="dialog"
      style={style}
    >
      {layout === "sheet" && (
        <div
          aria-hidden
          className="mx-auto mb-2 h-1 w-10 rounded-full bg-v3-surface-3"
        />
      )}
      <div className="flex items-center gap-1">
        <h2
          className="flex min-w-0 flex-1 items-center gap-2 text-[15px] font-semibold text-v3-warn-text dark:text-v3-warn"
          id={titleId}
        >
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full bg-v3-warn"
          />
          {title}
        </h2>
        {total > 1 && (
          <>
            <button
              aria-label={previousLabel}
              className={REVIEW_ICON_BUTTON}
              disabled={busy}
              type="button"
              onClick={onPrevious}
            >
              <ChevronLeft aria-hidden className="size-4" />
            </button>
            <span
              aria-live="polite"
              className="min-w-12 text-center font-mono text-[13px] tabular-nums text-v3-text-2"
              data-testid="review-card-position"
            >
              {positionLabel}
            </span>
            <button
              aria-label={nextLabel}
              className={REVIEW_ICON_BUTTON}
              disabled={busy}
              type="button"
              onClick={onNext}
            >
              <ChevronRight aria-hidden className="size-4" />
            </button>
          </>
        )}
        <button
          aria-label={closeLabel}
          className={REVIEW_ICON_BUTTON}
          disabled={busy}
          type="button"
          onClick={onClose}
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {total > 1 && (
        <div aria-hidden className="mt-2 flex gap-1">
          {Array.from({ length: total }, (_, item) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: one segment per position.
              key={item}
              className={cn(
                "h-1 flex-1 rounded-full",
                item === index ? "bg-v3-warn" : "bg-v3-surface-3",
              )}
            />
          ))}
        </div>
      )}
      <p
        className="mt-3 rounded-xl bg-v3-warn-tint px-3.5 py-3 text-[16px] leading-relaxed text-v3-text shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-warn)_35%,transparent)] wrap-break-word"
        data-testid="review-card-line"
      >
        {before && <span className="text-v3-text-2">{before}</span>}
        <strong className="font-semibold">{value}</strong>
        {after && <span className="text-v3-text-2">{after}</span>}
      </p>
      {explanation && (
        <p className="mt-3 border-l-2 border-v3-border-strong pl-3 text-[13.5px] leading-relaxed text-v3-text-2">
          {explanation}
        </p>
      )}
      {actions && <div className="mt-4">{actions}</div>}
    </div>
  );
}
